const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

export const sortOnboardingRowsNewestFirst = rows => [...rows].sort((left, right) => (
    right.date.localeCompare(left.date) || (left.order || 0) - (right.order || 0)
))

export const getDailyMetricAverages = (rows, fields) => Object.fromEntries(
    fields.map((field) => {
        const values = rows.map(row => row?.[field]).filter(Number.isFinite)
        return [field, {
            value: values.length > 0
                ? values.reduce((total, value) => total + value, 0) / values.length
                : null,
            days: values.length,
        }]
    })
)

const formatUtcDate = (date) => [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
].join('-')

const parseCalendarDate = (value) => {
    if (!DATE_PATTERN.test(value || '')) {
        return null
    }

    const date = new Date(`${value}T00:00:00Z`)

    return Number.isNaN(date.getTime()) || formatUtcDate(date) !== value
        ? null
        : date
}

export const getSeoulCalendarDate = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date)

    const year = parts.find(part => part.type === 'year')?.value
    const month = parts.find(part => part.type === 'month')?.value
    const day = parts.find(part => part.type === 'day')?.value

    return `${year}-${month}-${day}`
}

export const shiftCalendarDate = (value, days) => {
    const date = parseCalendarDate(value)

    if (!date) {
        return ''
    }

    date.setUTCDate(date.getUTCDate() + days)
    return formatUtcDate(date)
}

export const createOnboardingPresetRange = (days, now = new Date()) => {
    const normalizedDays = Number(days)

    if (!Number.isInteger(normalizedDays) || normalizedDays < 1 || normalizedDays > 90) {
        throw new RangeError('조회 기간 프리셋은 1일 이상 90일 이하여야 합니다.')
    }

    const yesterday = shiftCalendarDate(getSeoulCalendarDate(now), -1)

    return {
        from: shiftCalendarDate(yesterday, -(normalizedDays - 1)),
        to: yesterday,
    }
}

export const createInitialOnboardingRange = (now = new Date()) => (
    createOnboardingPresetRange(28, now)
)

export const getInclusiveDateRangeDays = (from, to) => {
    const fromDate = parseCalendarDate(from)
    const toDate = parseCalendarDate(to)

    if (!fromDate || !toDate) {
        return null
    }

    return Math.floor((toDate.getTime() - fromDate.getTime()) / MILLISECONDS_PER_DAY) + 1
}

export const validateOnboardingRange = (from, to, now = new Date()) => {
    const rangeDays = getInclusiveDateRangeDays(from, to)

    if (rangeDays === null) {
        return '시작 날짜와 종료 날짜를 YYYY-MM-DD 형식으로 선택하세요.'
    }

    if (rangeDays < 1) {
        return '시작 날짜는 종료 날짜보다 늦을 수 없습니다.'
    }

    if (rangeDays > 90) {
        return '조회 기간은 최대 90일까지 선택할 수 있습니다.'
    }

    if (to > shiftCalendarDate(getSeoulCalendarDate(now), -1)) {
        return '아직 마감되지 않은 오늘 이후 데이터는 조회할 수 없습니다.'
    }

    return null
}

export const flattenEntryRows = (summary) => (summary?.days || []).flatMap(day => (
    (day.entryByPlatform || []).map(entry => ({
        ...entry,
        date: day.date,
        dataStatus: day.dataStatus,
        warningCount: Array.isArray(day.warnings) ? day.warnings.length : 0,
    }))
))

export const getCombinedEntryRows = summary => sortOnboardingRowsNewestFirst(
    (summary?.days || []).flatMap(day => {
        const entries = (day.entryByPlatform || []).filter(entry => ['IOS', 'ANDROID'].includes(entry.platform))
        if (entries.length === 0) return []
        const totals = Object.fromEntries(['firstOpenUsers', 'signupStartedUsers', 'notStartedUsers'].map(field => [
            field, sumSafeIntegers(entries.map(entry => entry[field])),
        ]))
        return [{
            date: day.date,
            dataStatus: day.dataStatus,
            warningCount: Array.isArray(day.warnings) ? day.warnings.length : 0,
            ...totals,
            signupStartRate: calculateRangeRate(totals.signupStartedUsers, totals.firstOpenUsers),
            byPlatform: Object.fromEntries(entries.map(entry => [entry.platform, entry])),
        }]
    })
)

const ONBOARDING_FLOW_LABELS = {
    domestic_local: '국내 · 자체계정',
    domestic_sns: '국내 · SNS',
    global_local: '해외 · 자체계정',
    global_sns: '해외 · SNS',
}
const ONBOARDING_FLOW_KEYS = Object.keys(ONBOARDING_FLOW_LABELS)
const COUNTRY_SPECIAL_LABELS = {
    ALL: '전체',
    OTHER: '기타 국가',
    UNKNOWN: '국가 미분류',
}
const SNS_AUTH_BUCKETS = [
    { marketType: 'domestic', method: 'kakao' },
    { marketType: 'domestic', method: 'naver' },
    { marketType: 'domestic', method: 'google' },
    { marketType: 'domestic', method: 'apple' },
    { marketType: 'global', method: 'google' },
    { marketType: 'global', method: 'apple' },
]
const SNS_METHOD_LABELS = {
    kakao: '카카오',
    naver: '네이버',
    google: '구글',
    apple: '애플',
}

const HIGHEST_DROP_STEP_LABELS = {
    auth_method: '본인인증 방법 선택',
    contact_verification: '본인인증 진행',
    profile_form: '회원 정보 등록',
    terms: '약관 동의',
    signup_processing: '가입 완료 처리',
}

const HIGHEST_DROP_KIND_LABELS = {
    internal: '내부',
    transition: '전환',
}

export const getOnboardingKpiDates = (summary) => {
    const dataThrough = parseCalendarDate(summary?.meta?.dataThrough)
        ? summary.meta.dataThrough
        : null
    const dates = Array.isArray(summary?.days)
        ? summary.days
            .map(day => day?.date)
            .filter(date => parseCalendarDate(date) && (!dataThrough || date <= dataThrough))
        : []

    return [...new Set(dates)].sort((left, right) => right.localeCompare(left))
}

export const getLatestOnboardingKpiDate = summary => (
    getOnboardingKpiDates(summary)[0] || ''
)

const getOnboardingKpiDays = summary => (
    getOnboardingKpiDates(summary)
        .reverse()
        .map(date => summary.days.find(day => day?.date === date))
        .filter(Boolean)
)

const sumSafeIntegers = (values) => {
    if (!Array.isArray(values) || values.length === 0
        || values.some(value => !Number.isSafeInteger(value))) {
        return null
    }

    const total = values.reduce((sum, value) => sum + value, 0)

    return Number.isSafeInteger(total) ? total : null
}

const calculateRangeRate = (numerator, denominator) => (
    Number.isSafeInteger(numerator) && Number.isSafeInteger(denominator) && denominator !== 0
        ? numerator / denominator
        : null
)

const getRangeDataStatus = (days) => {
    const statuses = days.map(day => day?.dataStatus)

    if (statuses.includes('provisional')) {
        return 'provisional'
    }

    return statuses.length > 0 && statuses.every(status => status === 'final')
        ? 'final'
        : null
}

const normalizeIndividualCount = value => (
    Number.isSafeInteger(value) && value >= 0 ? value : null
)

const normalizeIndividualRate = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const rate = Number(value)
    return Number.isFinite(rate) && rate >= 0 ? rate : null
}

const normalizeHighestDrop = (candidate) => {
    const dropRate = normalizeIndividualRate(candidate?.dropRate)

    if (!candidate?.flowKey
        || !candidate?.stepName
        || !HIGHEST_DROP_KIND_LABELS[candidate.dropKind]
        || dropRate === null
        || dropRate <= 0) {
        return null
    }

    return {
        flowKey: candidate.flowKey,
        stepName: candidate.stepName,
        dropKind: candidate.dropKind,
        dropRate,
    }
}

export const getIndividualKpiForDate = (summary, date) => {
    const day = Array.isArray(summary?.days)
        ? summary.days.find(candidate => candidate?.date === date)
        : null

    if (!day?.individualKpi) {
        return null
    }

    return {
        date,
        dataStatus: day.dataStatus || null,
        warnings: Array.isArray(day.warnings) ? [...day.warnings] : [],
        cohortUsers: normalizeIndividualCount(day.individualKpi.cohortUsers),
        analyticsCompletedUsers: normalizeIndividualCount(
            day.individualKpi.analyticsCompletedUsers
        ),
        sameDayCompletionRate: normalizeIndividualRate(
            day.individualKpi.sameDayCompletionRate
        ),
        highestDrop: normalizeHighestDrop(day.individualKpi.highestDrop),
    }
}

export const getIndividualKpiForRange = (summary) => {
    const days = getOnboardingKpiDays(summary)
    const dailyKpis = days.map(day => getIndividualKpiForDate(summary, day.date))

    if (dailyKpis.length === 0 || dailyKpis.some(kpi => kpi === null)) {
        return null
    }

    const cohortUsers = sumSafeIntegers(dailyKpis.map(kpi => kpi.cohortUsers))
    const analyticsCompletedUsers = sumSafeIntegers(
        dailyKpis.map(kpi => kpi.analyticsCompletedUsers)
    )
    const highestDrop = dailyKpis
        .filter(kpi => kpi.highestDrop !== null)
        .map(kpi => ({ ...kpi.highestDrop, date: kpi.date }))
        .sort((left, right) => (
            right.dropRate - left.dropRate
            || right.date.localeCompare(left.date)
            || left.flowKey.localeCompare(right.flowKey)
        ))[0] || null

    return {
        dataStatus: getRangeDataStatus(days),
        warnings: days.flatMap(day => (
            Array.isArray(day.warnings) ? day.warnings.map(warning => ({ ...warning })) : []
        )),
        cohortUsers,
        analyticsCompletedUsers,
        sameDayCompletionRate: calculateRangeRate(
            analyticsCompletedUsers,
            cohortUsers
        ),
        highestDrop,
    }
}

const normalizeMemberTypeCount = value => (
    Number.isSafeInteger(value) ? value : null
)

const normalizeMemberTypeRate = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const rate = Number(value)
    return Number.isFinite(rate) ? rate : null
}

export const getMemberTypeForDate = (summary, date) => {
    const day = Array.isArray(summary?.days)
        ? summary.days.find(candidate => candidate?.date === date)
        : null

    if (!day?.memberType) {
        return null
    }

    return {
        signupStartUsers: normalizeMemberTypeCount(day.memberType.signupStartUsers),
        individualSelectedUsers: normalizeMemberTypeCount(
            day.memberType.individualSelectedUsers
        ),
        companySelectedUsers: normalizeMemberTypeCount(
            day.memberType.companySelectedUsers
        ),
        unselectedUsers: normalizeMemberTypeCount(day.memberType.unselectedUsers),
        selectionRate: normalizeMemberTypeRate(day.memberType.selectionRate),
    }
}

export const getMemberTypeForRange = (summary) => {
    const dailyMemberTypes = getOnboardingKpiDays(summary)
        .map(day => getMemberTypeForDate(summary, day.date))

    if (dailyMemberTypes.length === 0 || dailyMemberTypes.some(memberType => memberType === null)) {
        return null
    }

    const signupStartUsers = sumSafeIntegers(
        dailyMemberTypes.map(memberType => memberType.signupStartUsers)
    )
    const individualSelectedUsers = sumSafeIntegers(
        dailyMemberTypes.map(memberType => memberType.individualSelectedUsers)
    )
    const companySelectedUsers = sumSafeIntegers(
        dailyMemberTypes.map(memberType => memberType.companySelectedUsers)
    )
    const unselectedUsers = sumSafeIntegers(
        dailyMemberTypes.map(memberType => memberType.unselectedUsers)
    )
    const selectedUsers = Number.isSafeInteger(individualSelectedUsers)
        && Number.isSafeInteger(companySelectedUsers)
        ? individualSelectedUsers + companySelectedUsers
        : null

    return {
        signupStartUsers,
        individualSelectedUsers,
        companySelectedUsers,
        unselectedUsers,
        selectionRate: calculateRangeRate(selectedUsers, signupStartUsers),
    }
}

export const getFlowComparisonsForDate = (summary, date) => {
    const day = Array.isArray(summary?.days)
        ? summary.days.find(candidate => candidate?.date === date)
        : null
    const comparisons = day?.flowComparisons

    if (!Array.isArray(comparisons) || comparisons.length !== ONBOARDING_FLOW_KEYS.length) {
        return []
    }

    const normalized = comparisons.map((comparison) => {
        const completionRate = comparison?.completionRate

        if (!ONBOARDING_FLOW_LABELS[comparison?.flowKey]
            || !Number.isSafeInteger(comparison?.cohortUsers)
            || !Number.isSafeInteger(comparison?.completedUsers)
            || (completionRate !== null && !Number.isFinite(completionRate))
            || typeof comparison?.multiFlowWarning !== 'boolean') {
            return null
        }

        return {
            flowKey: comparison.flowKey,
            cohortUsers: comparison.cohortUsers,
            completedUsers: comparison.completedUsers,
            completionRate,
            multiFlowWarning: comparison.multiFlowWarning,
        }
    })
    const flowKeys = new Set(normalized.map(comparison => comparison?.flowKey))

    return normalized.some(comparison => comparison === null)
        || flowKeys.size !== ONBOARDING_FLOW_KEYS.length
        ? []
        : normalized
}

export const getFlowComparisonsForRange = (summary) => {
    const completeDailyComparisons = getOnboardingKpiDays(summary)
        .map(day => getFlowComparisonsForDate(summary, day.date))
        .filter(comparisons => comparisons.length === ONBOARDING_FLOW_KEYS.length)

    if (completeDailyComparisons.length === 0) {
        return []
    }

    return ONBOARDING_FLOW_KEYS.map((flowKey) => {
        const comparisons = completeDailyComparisons.map(daily => (
            daily.find(comparison => comparison.flowKey === flowKey)
        ))
        const cohortUsers = sumSafeIntegers(
            comparisons.map(comparison => comparison.cohortUsers)
        )
        const completedUsers = sumSafeIntegers(
            comparisons.map(comparison => comparison.completedUsers)
        )

        return {
            flowKey,
            cohortUsers,
            completedUsers,
            completionRate: calculateRangeRate(completedUsers, cohortUsers),
            multiFlowWarning: comparisons.some(comparison => comparison.multiFlowWarning),
        }
    }).sort((left, right) => {
        if (left.completionRate === null && right.completionRate === null) {
            return ONBOARDING_FLOW_KEYS.indexOf(left.flowKey)
                - ONBOARDING_FLOW_KEYS.indexOf(right.flowKey)
        }

        if (left.completionRate === null) {
            return 1
        }

        if (right.completionRate === null) {
            return -1
        }

        return right.completionRate - left.completionRate
            || ONBOARDING_FLOW_KEYS.indexOf(left.flowKey)
                - ONBOARDING_FLOW_KEYS.indexOf(right.flowKey)
    })
}

export const getFlowComparisonBarWidth = (completionRate) => {
    if (!Number.isFinite(completionRate)) {
        return 0
    }

    return Math.min(Math.max(completionRate * 100, 0), 100)
}

const normalizeCountryCount = value => (
    Number.isSafeInteger(value) && value >= 0 ? value : null
)

const normalizeCountryComparison = (country) => {
    const countryCode = String(country?.countryCode || '').trim().toUpperCase()
    const cohortUsers = normalizeCountryCount(country?.cohortUsers)
    const completedUsers = normalizeCountryCount(country?.completedUsers)
    const completionRate = normalizeIndividualRate(country?.completionRate)

    if ((!/^[A-Z]{2}$/.test(countryCode) && countryCode !== 'UNKNOWN')
        || cohortUsers === null
        || completedUsers === null
        || completedUsers > cohortUsers
        || (cohortUsers === 0 && completionRate !== null)
        || (cohortUsers > 0 && completionRate === null)) {
        return null
    }

    return {
        countryCode,
        cohortUsers,
        completedUsers,
        completionRate,
        detailAvailable: true,
    }
}

const calculateCountryCompletionRate = (completedUsers, cohortUsers) => (
    cohortUsers === 0 ? null : completedUsers / cohortUsers
)

export const getCountryComparisonsForDate = (summary, date, topCountryLimit = 5) => {
    const day = Array.isArray(summary?.days)
        ? summary.days.find(candidate => candidate?.date === date)
        : null
    const countries = Array.isArray(day?.countries) ? day.countries : []
    const normalizedLimit = Number(topCountryLimit)

    if (!Number.isInteger(normalizedLimit) || normalizedLimit < 1) {
        return []
    }

    const normalizedCountries = countries.map(normalizeCountryComparison)
    const countryCodes = new Set(normalizedCountries.map(country => country?.countryCode))

    if (normalizedCountries.length === 0
        || normalizedCountries.some(country => country === null)
        || countryCodes.size !== normalizedCountries.length) {
        return []
    }

    const unknown = normalizedCountries.find(country => country.countryCode === 'UNKNOWN')
        || {
            countryCode: 'UNKNOWN',
            cohortUsers: 0,
            completedUsers: 0,
            completionRate: null,
            detailAvailable: true,
        }
    const knownCountries = normalizedCountries
        .filter(country => country.countryCode !== 'UNKNOWN')
        .sort((left, right) => (
            right.cohortUsers - left.cohortUsers
            || left.countryCode.localeCompare(right.countryCode)
        ))
    const topCountries = knownCountries.slice(0, normalizedLimit)
    const otherCountries = knownCountries.slice(normalizedLimit)
    const otherCohortUsers = otherCountries.reduce((total, country) => (
        total + country.cohortUsers
    ), 0)
    const otherCompletedUsers = otherCountries.reduce((total, country) => (
        total + country.completedUsers
    ), 0)

    return [
        ...topCountries,
        {
            countryCode: 'OTHER',
            cohortUsers: otherCohortUsers,
            completedUsers: otherCompletedUsers,
            completionRate: calculateCountryCompletionRate(
                otherCompletedUsers,
                otherCohortUsers
            ),
            detailAvailable: false,
        },
        unknown,
    ]
}

export const getCountryComparisonsForRange = (summary, topCountryLimit = 5) => {
    const normalizedLimit = Number(topCountryLimit)

    if (!Number.isInteger(normalizedLimit) || normalizedLimit < 1) {
        return []
    }

    const countryTotals = new Map()

    for (const day of getOnboardingKpiDays(summary)) {
        const countries = Array.isArray(day.countries) ? day.countries : []
        const normalizedCountries = countries.map(normalizeCountryComparison)
        const countryCodes = new Set(normalizedCountries.map(country => country?.countryCode))

        if (normalizedCountries.some(country => country === null)
            || countryCodes.size !== normalizedCountries.length) {
            return []
        }

        for (const country of normalizedCountries) {
            const totals = countryTotals.get(country.countryCode) || {
                cohortUsers: 0,
                completedUsers: 0,
            }
            totals.cohortUsers += country.cohortUsers
            totals.completedUsers += country.completedUsers

            if (!Number.isSafeInteger(totals.cohortUsers)
                || !Number.isSafeInteger(totals.completedUsers)) {
                return []
            }

            countryTotals.set(country.countryCode, totals)
        }
    }

    if (countryTotals.size === 0) {
        return []
    }

    const normalizedCountries = [...countryTotals.entries()].map(([countryCode, totals]) => ({
        countryCode,
        cohortUsers: totals.cohortUsers,
        completedUsers: totals.completedUsers,
        completionRate: calculateCountryCompletionRate(
            totals.completedUsers,
            totals.cohortUsers
        ),
        detailAvailable: true,
    }))
    const unknown = normalizedCountries.find(country => country.countryCode === 'UNKNOWN')
        || {
            countryCode: 'UNKNOWN',
            cohortUsers: 0,
            completedUsers: 0,
            completionRate: null,
            detailAvailable: true,
        }
    const knownCountries = normalizedCountries
        .filter(country => country.countryCode !== 'UNKNOWN')
        .sort((left, right) => (
            right.cohortUsers - left.cohortUsers
            || left.countryCode.localeCompare(right.countryCode)
        ))
    const topCountries = knownCountries.slice(0, normalizedLimit)
    const otherCountries = knownCountries.slice(normalizedLimit)
    const otherCohortUsers = otherCountries.reduce((total, country) => (
        total + country.cohortUsers
    ), 0)
    const otherCompletedUsers = otherCountries.reduce((total, country) => (
        total + country.completedUsers
    ), 0)

    return [
        ...topCountries,
        {
            countryCode: 'OTHER',
            cohortUsers: otherCohortUsers,
            completedUsers: otherCompletedUsers,
            completionRate: calculateCountryCompletionRate(
                otherCompletedUsers,
                otherCohortUsers
            ),
            detailAvailable: false,
        },
        unknown,
    ]
}

const normalizeSnsCount = value => (
    Number.isSafeInteger(value) && value >= 0 ? value : null
)

const normalizeSnsRate = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const rate = Number(value)
    return Number.isFinite(rate) && rate >= 0 ? rate : null
}

const snsBucketKey = ({ marketType, method }) => `${marketType}:${method}`

export const getSnsAuthForDate = (summary, date) => {
    const day = Array.isArray(summary?.days)
        ? summary.days.find(candidate => candidate?.date === date)
        : null
    const sns = Array.isArray(day?.sns) ? day.sns : []

    if (sns.length !== SNS_AUTH_BUCKETS.length) {
        return []
    }

    const normalizedByBucket = new Map()

    for (const item of sns) {
        const marketType = String(item?.marketType || '').trim().toLowerCase()
        const method = String(item?.method || '').trim().toLowerCase()
        const key = snsBucketKey({ marketType, method })
        const selectedUsers = normalizeSnsCount(item?.selectedUsers)
        const successRate = normalizeSnsRate(item?.successRate)
        const cancelRate = normalizeSnsRate(item?.cancelRate)
        const errorRate = normalizeSnsRate(item?.errorRate)
        const newAccountRate = normalizeSnsRate(item?.newAccountRate)

        if (!SNS_AUTH_BUCKETS.some(bucket => snsBucketKey(bucket) === key)
            || normalizedByBucket.has(key)
            || selectedUsers === null) {
            return []
        }

        normalizedByBucket.set(key, {
            marketType,
            method,
            selectedUsers,
            successRate,
            cancelRate,
            errorRate,
            newAccountRate,
        })
    }

    return SNS_AUTH_BUCKETS.map(bucket => normalizedByBucket.get(snsBucketKey(bucket)))
        .filter(Boolean)
}

const SNS_RANGE_COUNT_FIELDS = [
    'selectedUsers',
    'successUsers',
    'cancelUsers',
    'errorUsers',
    'newAccountUsers',
]

export const getSnsAuthForRange = (summary) => {
    const totalsByBucket = new Map(SNS_AUTH_BUCKETS.map(bucket => [
        snsBucketKey(bucket),
        {
            marketType: bucket.marketType,
            method: bucket.method,
            ...Object.fromEntries(SNS_RANGE_COUNT_FIELDS.map(field => [field, 0])),
        },
    ]))
    let completeDayCount = 0

    for (const day of getOnboardingKpiDays(summary)) {
        const sns = Array.isArray(day.sns) ? day.sns : []

        if (sns.length === 0) {
            continue
        }

        if (sns.length !== SNS_AUTH_BUCKETS.length) {
            return []
        }

        const seenBuckets = new Set()

        for (const item of sns) {
            const marketType = String(item?.marketType || '').trim().toLowerCase()
            const method = String(item?.method || '').trim().toLowerCase()
            const key = snsBucketKey({ marketType, method })
            const totals = totalsByBucket.get(key)

            if (!totals || seenBuckets.has(key)
                || SNS_RANGE_COUNT_FIELDS.some(field => normalizeSnsCount(item?.[field]) === null)) {
                return []
            }

            seenBuckets.add(key)
            SNS_RANGE_COUNT_FIELDS.forEach((field) => {
                totals[field] += item[field]
            })

            if (SNS_RANGE_COUNT_FIELDS.some(field => !Number.isSafeInteger(totals[field]))) {
                return []
            }
        }

        completeDayCount += 1
    }

    if (completeDayCount === 0) {
        return []
    }

    return SNS_AUTH_BUCKETS.map((bucket) => {
        const totals = totalsByBucket.get(snsBucketKey(bucket))

        return {
            marketType: totals.marketType,
            method: totals.method,
            selectedUsers: totals.selectedUsers,
            successRate: calculateRangeRate(totals.successUsers, totals.selectedUsers),
            cancelRate: calculateRangeRate(totals.cancelUsers, totals.selectedUsers),
            errorRate: calculateRangeRate(totals.errorUsers, totals.selectedUsers),
            newAccountRate: calculateRangeRate(totals.newAccountUsers, totals.successUsers),
        }
    })
}

export const getStepCohortForRange = (stepSummary) => {
    const cohorts = Array.isArray(stepSummary?.days)
        ? stepSummary.days.map(day => day?.cohort).filter(Boolean)
        : []

    if (cohorts.length === 0) {
        return null
    }

    const users = sumSafeIntegers(cohorts.map(cohort => cohort.users))
    const analyticsCompletedUsers = sumSafeIntegers(
        cohorts.map(cohort => cohort.analyticsCompletedUsers)
    )

    if (users === null || analyticsCompletedUsers === null) {
        return null
    }

    return {
        users,
        analyticsCompletedUsers,
        sameDayCompletionRate: calculateRangeRate(analyticsCompletedUsers, users),
    }
}

export const formatCountry = (countryCode) => {
    const normalizedCountryCode = String(countryCode || '').trim().toUpperCase()

    if (COUNTRY_SPECIAL_LABELS[normalizedCountryCode]) {
        return COUNTRY_SPECIAL_LABELS[normalizedCountryCode]
    }

    if (!/^[A-Z]{2}$/.test(normalizedCountryCode)) {
        return normalizedCountryCode || '-'
    }

    try {
        if (typeof Intl.DisplayNames === 'function') {
            const displayName = new Intl.DisplayNames(['ko'], { type: 'region' })
                .of(normalizedCountryCode)

            if (displayName && displayName !== normalizedCountryCode) {
                return displayName
            }
        }
    } catch (_error) {
        // 국가명 변환을 지원하지 않는 환경에서는 ISO 코드를 그대로 사용한다.
    }

    return normalizedCountryCode
}

export const formatSnsMethod = method => (
    SNS_METHOD_LABELS[String(method || '').trim().toLowerCase()] || method || '-'
)

export const formatOnboardingFlow = flowKey => (
    ONBOARDING_FLOW_LABELS[flowKey] || flowKey || '-'
)

export const formatHighestDropStep = stepName => (
    HIGHEST_DROP_STEP_LABELS[stepName] || stepName || '-'
)

export const formatHighestDropKind = dropKind => (
    HIGHEST_DROP_KIND_LABELS[dropKind] || dropKind || '-'
)

const isWarningForStep = (warning, step) => {
    if (!warning || typeof warning !== 'object') {
        return true
    }

    const orderMatches = warning.stepOrder === null
        || warning.stepOrder === undefined
        || Number(warning.stepOrder) === Number(step.order)
    const nameMatches = !warning.stepName || warning.stepName === step.stepName

    return orderMatches && nameMatches
}

export const flattenStepRows = (summary) => (summary?.days || []).flatMap(day => (
    (day.steps || []).map((step) => {
        const warnings = Array.isArray(day.warnings)
            ? day.warnings.filter(warning => isWarningForStep(warning, step))
            : []

        return {
            ...step,
            date: day.date,
            dataStatus: day.dataStatus,
            warnings,
            warningCount: warnings.length,
        }
    })
))

export const getHighestDropByDate = (rows) => (rows || []).reduce((highestByDate, row) => {
    if (!row?.date) {
        return highestByDate
    }

    const candidates = [
        { dropKind: 'internal', dropRate: Number(row.internalDropRate) },
        { dropKind: 'transition', dropRate: Number(row.transitionDropRate) },
    ]

    candidates.forEach((candidate) => {
        const current = highestByDate[row.date]

        if (Number.isFinite(candidate.dropRate)
            && candidate.dropRate > 0
            && (!current || candidate.dropRate > current.dropRate)) {
            highestByDate[row.date] = {
                order: row.order,
                stepName: row.stepName,
                dropKind: candidate.dropKind,
                dropRate: candidate.dropRate,
            }
        }
    })

    return highestByDate
}, {})

export const formatQualityWarning = (warning) => {
    if (typeof warning === 'string') {
        return warning
    }

    if (!warning || typeof warning !== 'object') {
        return '세부 경고 정보가 없습니다.'
    }

    const code = warning.code ? `[${warning.code}] ` : ''
    return `${code}${warning.message || '세부 경고 정보가 없습니다.'}`
}

export const formatPlatform = (platform) => ({
    IOS: 'iOS',
    ANDROID: 'Android',
}[platform] || platform || '-')

export const formatRate = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }

    const rate = Number(value)
    return Number.isFinite(rate) ? `${(rate * 100).toFixed(1)}%` : '-'
}

export const formatEntryFrequency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }

    const frequency = Number(value)
    return Number.isFinite(frequency) ? `${frequency.toFixed(2)}회/명` : '-'
}

export const formatOptionalCount = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }

    const count = Number(value)
    return Number.isFinite(count) ? new Intl.NumberFormat().format(count) : '-'
}

export const formatRefreshedAt = (value) => {
    if (!value) {
        return '-'
    }

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '-'
    }

    return new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)
}
